import React, { useCallback, useContext, useState } from 'react';
import { StripeMode } from '../../types/pricingPage';
import { CurrentCompanyContext } from '../../hooks/CurrentCompanyContext';
import { useToasts } from 'react-toast-notifications';
import { useAxios } from '../../utils/hooks';
import { FormSection, FormSectionBody, FormSectionHeader, StyledForm } from '../../components/Form';
import { Field, Form } from 'react-final-form';
import VALIDATORS, { composeValidators } from '../../utils/validators';
import Error from '../../components/Error';
import {
    TableHeadResponsive,
    TableHeadThResponsive,
    TableResponsive,
    TableRowResponsive,
    TableRowTdResponsive,
} from '../../components/TableResponsive';
import { formatDatePretty } from '../../utils/dates';
import Button from '../../components/Button';

const isCurrentPeriod = (usage: UsageReportResponse) => !usage.period.start && !usage.period.end;

interface UsageReportResponse {
    id: string;
    object: string;
    invoice: string;
    livemode: boolean;
    period: { start: number; end: number };
    subscription_item: string;
    total_usage: number;
}

interface FormData {
    mode?: StripeMode;
    subscriptionItemId: string;
    quantity: number;
    action: 'set' | 'increment';
    timestamp?: number;
}

interface Props {
    stripeMode: StripeMode;
    subscriptionId?: string;
}

export default function UsageForm({ stripeMode, subscriptionId }: Props) {
    const company = useContext(CurrentCompanyContext);
    const { addToast } = useToasts();
    const axios = useAxios();
    const [response, setResponse] = useState<UsageReportResponse[]>();

    const handleSubmit = useCallback(
        (data: FormData) => {
            if (!axios || !axios.current) {
                return Promise.resolve();
            }
            if (window.confirm(`Are you sure you want to report quantity of ${data.quantity}?`)) {
                return axios.current
                    ?.post<UsageReportResponse[]>(
                        '/subscriptions/usage',
                        { ...data, mode: stripeMode },
                        {
                            headers: { Authorization: `Bearer ${company?.apiKey}` },
                        }
                    )
                    .then((res) => {
                        addToast('Usage reported successfully.', { appearance: 'success' });
                        setResponse(res.data);
                    })
                    .catch((err) => {
                        console.error(err);
                        addToast('Something went wrong. Please try again.', { appearance: 'error' });
                    });
            }
        },
        [addToast, axios, company?.apiKey, stripeMode]
    );

    return (
        <>
            <Form<FormData>
                initialValues={{ action: 'increment', mode: stripeMode, subscriptionItemId: subscriptionId }}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, submitting, invalid }) => (
                    <StyledForm onSubmit={handleSubmit} className="w-full mt-4 shadow">
                        <FormSection>
                            <FormSectionHeader>Subscription Usage</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Stripe Subscription or Item ID</label>
                                    <Field
                                        name="subscriptionItemId"
                                        component="input"
                                        type="text"
                                        validate={VALIDATORS.required}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input
                                                    {...input}
                                                    placeholder="si_Omp3GIh4O51akV"
                                                    className="input w-full"
                                                />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Quantity (hours, units, items etc)</label>
                                    <Field
                                        name="quantity"
                                        component="input"
                                        type="number"
                                        validate={composeValidators(VALIDATORS.required, VALIDATORS.min(0))}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input
                                                    {...input}
                                                    placeholder="1000"
                                                    className="input block w-1/2 md:w-1/4"
                                                />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Action</label>
                                    <div className="flex justify-start space-x-2">
                                        <div className="form-check form-check-inline">
                                            <Field name="action" type="radio" value="increment">
                                                {(props) => (
                                                    <label>
                                                        <input {...props.input} className="form-radio mr-2" />
                                                        <span className="form-check-label inline-block text-gray-800">
                                                            Increment quantity
                                                        </span>
                                                    </label>
                                                )}
                                            </Field>
                                        </div>
                                        <div className="form-check form-check-inline">
                                            <Field name="action" type="radio" value="set">
                                                {(props) => (
                                                    <label>
                                                        <input {...props.input} className="form-radio mr-2" />
                                                        <span className="form-check-label inline-block text-gray-800">
                                                            Override quantity
                                                        </span>
                                                    </label>
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex">
                                    <Button
                                        type="submit"
                                        kind="tertiary"
                                        className="ml-auto"
                                        state={submitting ? 'SAVING' : 'READY'}
                                        disabled={invalid}
                                    >
                                        Report Usage
                                    </Button>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                    </StyledForm>
                )}
            </Form>
            {response && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg max-w-4xl mx-auto mt-8">
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">Total Usage</h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500 mb-2">
                            Subscription id{' '}
                            <a
                                href={`https://dashboard.stripe.com/test/subscription_items/${response[0].subscription_item}/usage_records`}
                                className="link"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {response[0].subscription_item}
                            </a>
                        </p>

                        <TableResponsive>
                            <TableHeadResponsive>
                                <TableRowResponsive>
                                    <TableHeadThResponsive>Period</TableHeadThResponsive>
                                    <TableHeadThResponsive>Total Usage (Quantity)</TableHeadThResponsive>
                                </TableRowResponsive>
                            </TableHeadResponsive>
                            <tbody className="text-gray-600 text-sm font-light">
                                {response.map((row) => (
                                    <TableRowResponsive>
                                        <TableRowTdResponsive>
                                            <span className="font-medium text-base">
                                                {isCurrentPeriod(row)
                                                    ? 'Unbilled charges'
                                                    : `From ${formatDatePretty(
                                                          row.period.start,
                                                          '-'
                                                      )} to ${formatDatePretty(row.period.end, '-')}`}
                                            </span>
                                        </TableRowTdResponsive>
                                        <TableRowTdResponsive>
                                            <span className="font-bold text-base">{row.total_usage}</span>
                                        </TableRowTdResponsive>
                                    </TableRowResponsive>
                                ))}
                            </tbody>
                        </TableResponsive>
                    </div>
                </div>
            )}
        </>
    );
}
